// frontend/src/lib/email-draft.ts
// Deterministic email-draft template engine (the "fallback" half of the hybrid
// email flow — see plans/audit-command-center-enhancements-plan.md §2.1/§2.2).
//
// This is presentation/correspondence, not business logic (Rule 10): it formats
// vendor + finding data into a formal audit-correspondence reply. No thresholds
// or business decisions live here. All figures come from the card/finding data
// passed in at runtime — nothing is hardcoded per vendor or per finding.

import type { PipelineStage } from '@/data/seed/types'

// ─── Public types ────────────────────────────────────────────────────────────

export interface DraftVendor {
  id: string
  name: string
  full_name: string
  contact_email: string
}

export interface DraftFinding {
  id: string
  title: string
  amount: number
  narrative: string
  classification: string
  linked_audit_id?: string
}

export interface DraftInput {
  vendor: DraftVendor
  finding: DraftFinding
  stage: PipelineStage
  /** Engagement-level audit id, e.g. 'AUD-CONCORDIA-01'. Derived if omitted. */
  auditId?: string
  /** Optional extra figures (e.g. from a workbench audit's key_figures). */
  keyFigures?: Record<string, number | string>
}

export interface EmailDraft {
  subject: string
  body: string
  to: string
}

// ─── Stage → email copy map (plan §2.2) ──────────────────────────────────────
// `preliminary` controls whether the "no remittance due" disclaimer is included
// (dropped once a finding is Recovered or Closed).

interface StageCopy {
  intro: string
  ask: string
  preliminary: boolean
}

export const STAGE_EMAIL_COPY: Record<PipelineStage, StageCopy> = {
  Identified: {
    intro: 'our review has identified a finding we would like to bring to your attention',
    ask: 'To begin our review, please provide the supporting documentation for this finding (invoices, PO records, work orders, or third-party receipts as applicable).',
    preliminary: true,
  },
  'Under Review': {
    intro: 'our review of this finding is in progress',
    ask: 'To progress this finding, please provide the specific supporting documentation it requires so we can reconcile the flagged amount.',
    preliminary: true,
  },
  Negotiating: {
    intro: 'we are working with you toward a resolution of this finding',
    ask: 'We would like to agree a reconciliation path for the recoverable-if-confirmed amount. Please confirm your position (Agree, Partially Agree, or Disagree) and attach any supporting documentation.',
    preliminary: true,
  },
  Recovered: {
    intro: 'this finding has now been resolved',
    ask: 'Thank you for the credit or remittance received against this finding. Please confirm the closing entry so we can mark it closed on our side.',
    preliminary: false,
  },
  Closed: {
    intro: 'this finding has been closed',
    ask: 'This finding is now closed with its final determination recorded. We appreciate your cooperation throughout the audit.',
    preliminary: false,
  },
}

const AUDIT_PERIOD = 'FY2023–FY2025'
const SIGNATURE = 'Regards,\nSupervity · ROI Assurance, on behalf of T-Mobile USA, Inc.'

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format a dollar figure as `$415,648` (no decimals, thousands separators). */
export function formatUsd(amount: number): string {
  const sign = amount < 0 ? '-' : ''
  return `${sign}$${Math.abs(amount).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

/** Strip common corporate suffixes for a natural salutation ("Concordia Wireless"). */
export function salutationName(fullName: string, fallback?: string): string {
  const stripped = fullName
    .replace(/,?\s+(Inc\.?|LLC|Ltd\.?|Co\.,?\s*Ltd\.?|Oy|SAS|AS|GmbH|S\.A\.?|Corp\.?|Corporation)$/i, '')
    .trim()
  return stripped || fallback || fullName
}

/** Derive the engagement-level audit id from the vendor, e.g. 'AUD-CONCORDIA-01'. */
export function engagementAuditId(vendor: DraftVendor): string {
  const key = vendor.name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-')
  return `AUD-${key}-01`
}

// ─── Builder ──────────────────────────────────────────────────────────────────

export function buildEmailDraft(input: DraftInput): EmailDraft {
  const { vendor, finding, stage } = input
  const auditId = input.auditId || engagementAuditId(vendor)
  const copy = STAGE_EMAIL_COPY[stage] ?? STAGE_EMAIL_COPY['Under Review']
  const salutation = salutationName(vendor.full_name, vendor.name)
  const amountLabel = formatUsd(finding.amount)

  const subject = `${auditId} — Finding ${finding.id} ${finding.title} (${amountLabel})`

  const openingLine = `As part of T-Mobile's ${AUDIT_PERIOD} recovery audit (${auditId}), ${copy.intro} — ${finding.title} (Finding ${finding.id}, ${amountLabel}).`

  const disclaimer = copy.preliminary
    ? 'This is a preliminary finding; no remittance is due at this time. '
    : ''

  const closingParagraph = `${disclaimer}${copy.ask} Please reference ${auditId} on all correspondence.`

  const body = [
    `Dear ${salutation} team,`,
    '',
    openingLine,
    '',
    finding.narrative,
    '',
    closingParagraph,
    '',
    SIGNATURE,
  ].join('\n')

  return { subject, body, to: vendor.contact_email }
}
